import { Card, EmptyState, Field, PageHeader } from '@catering/ui';
import { Link } from '@tanstack/react-router';
import { HelpCircle, Search } from 'lucide-react';
import { type JSX, useMemo, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser, type CurrentUser } from '../../auth/use-current-user';
import { GUIDE_MODULES } from '../../guide/content';
import { GLOSSARY_TERMS } from '../../guide/glossary';
import { searchGuide, type GuideSearchResult } from '../../guide/search';
import type { GuideModule } from '../../guide/types';

function sortByPriority(modules: readonly GuideModule[]): GuideModule[] {
  return [...modules].sort(
    (a, b) => (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER),
  );
}

function visibleModulesFor(currentUser: CurrentUser | undefined): GuideModule[] {
  return GUIDE_MODULES.filter(
    (module) =>
      module.requiredPermission === null || hasPermission(currentUser, module.requiredPermission),
  );
}

/** Human label for the section a result was matched in, shown next to the module title so a hit is never ambiguous about where it lives. */
const SECTION_LABEL_BY_KIND: Record<GuideSearchResult['kind'], string> = {
  title: 'Descripción general',
  step: 'Paso a paso',
  faq: 'Preguntas frecuentes',
  glossary: 'Glosario',
};

/** Matches the anchor ids `GuideModulePage` renders (`#steps`, `#faq`); a title match has no specific anchor, so it lands at the top of the page. */
const SECTION_HASH_BY_KIND: Partial<Record<GuideSearchResult['kind'], string>> = {
  step: 'steps',
  faq: 'faq',
};

interface SearchResultCardProps {
  result: GuideSearchResult;
}

function SearchResultCard({ result }: SearchResultCardProps): JSX.Element {
  const sectionLabel = SECTION_LABEL_BY_KIND[result.kind];
  const contextLabel =
    result.kind === 'glossary' ? sectionLabel : `${result.moduleTitle} · ${sectionLabel}`;

  const body = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-primary-600">{contextLabel}</p>
      <p className="text-sm font-semibold text-slate-900">{result.label}</p>
      <p className="text-sm text-slate-600">{result.snippet}</p>
    </>
  );

  if (result.kind === 'glossary') {
    return (
      <Link to="/guia/glosario">
        <Card className="flex flex-col gap-1 transition-colors hover:border-primary-300">
          {body}
        </Card>
      </Link>
    );
  }

  const hash = SECTION_HASH_BY_KIND[result.kind];

  return (
    <Link
      to="/guia/$moduleId"
      params={{ moduleId: result.moduleId }}
      {...(hash === undefined ? {} : { hash })}
    >
      <Card className="flex flex-col gap-1 transition-colors hover:border-primary-300">{body}</Card>
    </Link>
  );
}

interface ModuleCardProps {
  module: GuideModule;
}

function ModuleCard({ module }: ModuleCardProps): JSX.Element {
  const Icon = module.icon;

  return (
    <Link
      to="/guia/$moduleId"
      params={{ moduleId: module.id }}
      aria-label={`Ver guía de ${module.title}`}
      className="block"
    >
      <Card className="flex flex-col gap-2 transition-colors hover:border-primary-300">
        <span className="flex size-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <Icon aria-hidden="true" className="size-5" />
        </span>
        <h3 className="text-sm font-semibold text-slate-900">{module.title}</h3>
        <p className="line-clamp-4 text-sm text-slate-600">{module.summary}</p>
      </Card>
    </Link>
  );
}

/** `guide-navigation/spec.md` → "Pantalla índice /guia" and `guide-search/spec.md`. */
export function GuideListPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const [query, setQuery] = useState('');

  const visibleModules = useMemo(
    () => sortByPriority(visibleModulesFor(currentUser.data)),
    [currentUser.data],
  );

  const trimmedQuery = query.trim();
  const searchResults = useMemo(
    () => (trimmedQuery === '' ? [] : searchGuide(query, visibleModules, GLOSSARY_TERMS)),
    [query, trimmedQuery, visibleModules],
  );

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={HelpCircle}
        title="Guía"
        description="Consultá cómo usar cada módulo del sistema."
      />

      <Card>
        <Field
          type="search"
          role="searchbox"
          name="guide-search"
          label="Buscar en la guía"
          placeholder="Buscar por ejemplo: corte, cupo, dietaria..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </Card>

      {trimmedQuery === '' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleModules.map((module) => (
            <ModuleCard key={module.id} module={module} />
          ))}
        </div>
      ) : searchResults.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No encontramos resultados"
          description={`Probá con otro término distinto de "${query}".`}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {searchResults.map((result, index) => (
            <SearchResultCard key={`${result.kind}-${result.moduleId}-${index}`} result={result} />
          ))}
        </div>
      )}
    </section>
  );
}
